import { type MouseEvent } from 'react';
import { type IntlShape } from 'react-intl';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import {
    GA4EventName,
    GA4FeatureCategory,
    type GA4ItemListName,
    type Product as CartProduct,
    ProductType as TrackingProductType,
    trackAnalyticsEvent,
    trackProductTileServiceInteraction,
} from '@jsmdg/tracking';
import { DiscountBadgeShape, SlidingDirections } from '@jsmdg/yoshi';
import { pageConfig, PageType } from '../../shared/enums/pageType';
import { ProductType } from '../../shared/enums/productType';
import { type ContentBlock } from '../../shared/types/contentBlock';
import { type DiscountBadge } from '../../shared/types/discountBadge';
import { type FragmentContext } from '../../shared/types/fragmentContext';
import { type Image, type Price, type Product } from '../../shared/types/searchResponse';
import { enhanceUrlWithSearchParams } from '../helper/enhanceUrlWithSearchParams';
import { type ProductTileMessagesType } from '../helper/getProductTileMessages';

const trackingProductTypeMap: Record<ProductType, TrackingProductType> = {
    [ProductType.ExperienceProduct]: TrackingProductType.Experience,
    [ProductType.BoxProduct]: TrackingProductType.Box,
    [ProductType.ValueVoucherProduct]: TrackingProductType.ValueVoucher,
};

type UseProductTileDataReturnType = {
    locale: string;
    images: Image[];
    services: ContentBlock;
    url: string;
    hasClosestLocation: boolean;
    price: Price;
    originalPrice?: Price;
    discountBadge?: DiscountBadge;
    productTrackingData: CartProduct;
    onLinkTextClick: (willShowServices: boolean, clickText: string) => Promise<void>;
    onCloseServices: () => Promise<void>;
    onLearnMoreClick: (clickText: string, event?: MouseEvent) => Promise<void>;
    onImageSliderSlide: (direction: SlidingDirections) => Promise<void>;
    getLocationLabel: () => string | undefined;
    getParticipantsLabel: () => string;
    getPricePrefix: () => string | undefined;
};

const useProductTileData = (
    product: Product,
    intl: IntlShape,
    listInfo: {
        indexName?: string;
        queryId?: string;
        trackingListName?: GA4ItemListName;
        hasLocationFilter?: boolean;
    },
    messages?: ProductTileMessagesType,
): UseProductTileDataReturnType => {
    const { hasLocationFilter } = listInfo;

    const { locale, tenantConfig, isMydays } = useFragmentContext<FragmentContext>();

    const {
        productId,
        title,
        manufacturerSku,
        images,
        productType,
        trackingName,
        trackingPath,
        rating,
        participants,
        locations,
        classifications,
        closestLocation,
        services,
        hasPriceRange,
    } = product;

    const { showDiscount } = pageConfig[PageType.Home] ?? { showDiscount: false };

    const price = showDiscount ? product.price : product.originalPrice || product.price;
    const originalPrice = showDiscount ? product.originalPrice : undefined;

    const discountBadge =
        showDiscount && product.discountBadge
            ? {
                  ...product.discountBadge,
                  withHint: isMydays,
                  className: isMydays ? 'ml-0-5x mt-0-5x ml-xs-1x mt-xs-1x' : '',
                  shape: isMydays ? DiscountBadgeShape.Pill : DiscountBadgeShape.Rectangle,
              }
            : undefined;

    const productTrackingData: CartProduct = {
        id: productId,
        trackingName,
        url: product.url,
        productType: trackingProductTypeMap[productType],
        title,
        trackingPath,
        manufacturerSku,
        price: {
            gross: {
                amount: Number(price.gross.amount),
                currencyCode: price.gross.currencyCode,
            },
        },
        originalPrice: originalPrice && {
            gross: {
                amount: Number(originalPrice.gross.amount),
                currencyCode: originalPrice.gross.currencyCode,
            },
        },
        discountBadgeId: discountBadge?.id,
        discountBadgeName: discountBadge?.text,
        rating,
        locationCount: locations?.length,
    };

    if (participants?.min && participants?.max) {
        productTrackingData.participants = {
            min: participants.min,
            max: participants.max,
        };
    }

    if (hasLocationFilter) {
        productTrackingData.closestLocationDistance = closestLocation?.distance.value;
        productTrackingData.locationCount = undefined;
    }

    const isOnlineExperience =
        classifications?.includes('LocationOnline') &&
        locations?.some(location =>
            tenantConfig?.onlineExperienceOptions.includes(location.name as string),
        );

    const hasClosestLocation = !!closestLocation && !isOnlineExperience;

    const onLinkTextClick = async (willShowServices: boolean, clickText: string): Promise<void> => {
        await window.yieldToMainThread();
        trackProductTileServiceInteraction(
            'ProductTile',
            willShowServices ? 'Expand' : 'Collapse',
            'Services',
            {
                eventName: GA4EventName.ClickTextLink,
                feature_category: GA4FeatureCategory.ProductList,
                click_element: willShowServices ? 'Producttile expand' : 'Producttile close',
                click_text: clickText,
                product_title: product.title,
            },
        );
    };

    const onCloseServices = async (): Promise<void> => {
        await window.yieldToMainThread();
        trackProductTileServiceInteraction('ProductTile', 'Close', 'Services', {
            eventName: GA4EventName.ClickTextLink,
            feature_category: GA4FeatureCategory.ProductList,
            click_element: 'Producttile close',
            click_text: 'X',
            product_title: product.title,
        });
    };

    const onLearnMoreClick = async (clickText: string, event?: MouseEvent): Promise<void> => {
        event?.preventDefault();
        window.location.href = enhanceUrlWithSearchParams(product.url);
        await window.yieldToMainThread();
        trackProductTileServiceInteraction('ProductTile', 'ButtonClick', 'More information', {
            eventName: GA4EventName.ClickButton,
            feature_category: GA4FeatureCategory.ProductList,
            click_element: 'Producttile more information',
            click_text: clickText,
            click_url: window.location.origin + product.url,
            product_title: product.title,
        });
    };

    const onImageSliderSlide = async (direction: SlidingDirections): Promise<void> => {
        await window.yieldToMainThread();
        trackAnalyticsEvent({
            category: 'ProductTile',
            action: 'Slide',
            label: direction,
            eventData: {
                eventName: GA4EventName.SwitchPicture,
                feature_category: GA4FeatureCategory.ProductList,
                click_element:
                    direction === SlidingDirections.Next ? 'Slide next' : 'Slide previous',
                product_title: product.title,
            },
        });
    };

    const getLocationLabel = (): string | undefined => {
        if (!locations?.length || !messages) {
            return undefined;
        }

        if (hasClosestLocation) {
            return closestLocation.name;
        }

        if (isOnlineExperience) {
            return intl.formatMessage(messages.onlineExperienceLocation);
        }

        if (locations.length > 1) {
            return intl.formatMessage(messages.multipleLocations, {
                locationCount: locations.length,
            });
        }

        return locations[0].name;
    };

    const getParticipantsLabel = (): string => {
        if (!participants || !messages) {
            return '';
        }

        const { min, max } = participants;

        return intl.formatMessage(messages.participantsLabel, {
            participants: min === max ? max : `${min as number}-${max as number}`,
        });
    };

    const getPricePrefix = (): string | undefined => {
        if (productType !== ProductType.ValueVoucherProduct || !hasPriceRange || !messages) {
            return undefined;
        }

        return intl.formatMessage(messages.pricePrefix);
    };

    return {
        locale,
        images,
        services,
        url: product.url,
        hasClosestLocation,
        price,
        originalPrice,
        discountBadge,
        productTrackingData,
        onLinkTextClick,
        onCloseServices,
        onLearnMoreClick,
        onImageSliderSlide,
        getLocationLabel,
        getParticipantsLabel,
        getPricePrefix,
    };
};

export { useProductTileData };
